/**
 * HTTP client FITLAB.
 *
 * Todas las URL son RELATIVAS (`/api/...`) para que el proxy Vite (dev) y el
 * mismo origen en producción (Firebase Hosting → Cloud Run rewrite) funcionen
 * sin URLs absolutas hardcodeadas (anti-parche — regla global Wipxap).
 *
 * PHI: nunca loggear `body` ni `response` completos — solo status/code. El
 * interceptor 401 centraliza la expulsión al login.
 */

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export interface ApiErrorBody {
  error: string
  [k: string]: unknown
}

export class ApiError extends Error {
  readonly status: number
  readonly body: ApiErrorBody | null

  constructor(status: number, body: ApiErrorBody | null, message?: string) {
    super(message || body?.error || `HTTP ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

/* ---- Token store (in-memory + sessionStorage fallback) --------------------- */

const TOKEN_KEY = 'fitlab.jwt'

let memoryToken: string | null = null

export function getToken(): string | null {
  if (memoryToken) return memoryToken
  try {
    // sessionStorage: sólo JWT (no PHI). Se limpia al cerrar pestaña.
    const stored = window.sessionStorage.getItem(TOKEN_KEY)
    if (stored) memoryToken = stored
    return memoryToken
  } catch {
    return memoryToken
  }
}

export function setToken(token: string | null): void {
  memoryToken = token
  try {
    if (token) window.sessionStorage.setItem(TOKEN_KEY, token)
    else window.sessionStorage.removeItem(TOKEN_KEY)
  } catch {
    /* sessionStorage no disponible → memoria suficiente */
  }
}

/* ---- 401 interceptor hook -------------------------------------------------- */

type UnauthorizedHandler = () => void
let onUnauthorized: UnauthorizedHandler | null = null
export function setUnauthorizedHandler(fn: UnauthorizedHandler | null): void {
  onUnauthorized = fn
}

/* ---- Core request ---------------------------------------------------------- */

interface RequestOptions {
  method?: HttpMethod
  body?: unknown
  headers?: Record<string, string>
  /** Si true, no adjunta Authorization header (para /login). */
  anonymous?: boolean
  signal?: AbortSignal
  /** Para FormData / multipart uploads. */
  rawBody?: BodyInit
}

export async function apiRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const token = opts.anonymous ? null : getToken()
  const isRaw = opts.rawBody != null
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(isRaw ? {} : { 'Content-Type': 'application/json' }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(opts.headers ?? {}),
  }

  const res = await fetch(path, {
    method: opts.method ?? 'GET',
    headers,
    body: isRaw ? opts.rawBody : opts.body != null ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  })

  if (res.status === 401 && !opts.anonymous) {
    setToken(null)
    onUnauthorized?.()
  }

  let parsed: unknown = null
  const contentType = res.headers.get('content-type') || ''
  if (contentType.includes('application/json')) {
    try {
      parsed = await res.json()
    } catch {
      parsed = null
    }
  } else if (res.status !== 204) {
    // respuesta binaria (xlsx/csv) — devolver blob en caller con apiRequestBlob
    parsed = null
  }

  if (!res.ok) {
    const body = (parsed as ApiErrorBody | null) ?? { error: `http_${res.status}` }
    throw new ApiError(res.status, body)
  }

  return parsed as T
}

export async function apiRequestBlob(
  path: string,
  opts: RequestOptions = {},
): Promise<{ blob: Blob; headers: Headers }> {
  const token = opts.anonymous ? null : getToken()
  const isRaw = opts.rawBody != null
  const headers: Record<string, string> = {
    ...(isRaw ? {} : { 'Content-Type': 'application/json' }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(opts.headers ?? {}),
  }

  const res = await fetch(path, {
    method: opts.method ?? 'GET',
    headers,
    body: isRaw ? opts.rawBody : opts.body != null ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  })

  if (res.status === 401) {
    setToken(null)
    onUnauthorized?.()
  }

  if (!res.ok) {
    let body: ApiErrorBody | null = null
    try {
      body = (await res.json()) as ApiErrorBody
    } catch {
      body = { error: `http_${res.status}` }
    }
    throw new ApiError(res.status, body)
  }

  const blob = await res.blob()
  return { blob, headers: res.headers }
}

/* ---- Sugar API ------------------------------------------------------------- */

export const api = {
  get: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body' | 'rawBody'>) =>
    apiRequest<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...opts, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...opts, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...opts, method: 'PUT', body }),
  delete: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body' | 'rawBody'>) =>
    apiRequest<T>(path, { ...opts, method: 'DELETE' }),
}
